export const privacyIntroduction = [
  'GenMoney is a financial education company. We provide educational workshops, webinars, content and insights to UK employers, universities and colleges. We do not provide financial advice and we are not authorised or regulated by the Financial Conduct Authority (FCA).',
  'This notice explains what personal data we collect, why we collect it, how we use it, and your rights under UK GDPR and the Data Protection Act 2018.',
];

export const privacySections = [
  {
    title: '1. Who we are',
    paragraphs: [
      'Controller: Generation Money Limited, trading as GenMoney',
      'Registered address: 124 City Road, London, EC1V 2NX',
      'Company number: 12862017',
      'Contact for privacy matters: hello@genmoney.co.uk',
    ],
  },
  {
    title: '2. What personal data we collect',
    groups: [
      ['a) Enquiries and business contacts', 'Name, job title, organisation, work email, phone number, LinkedIn profile details that you have made public, and details of our conversations with you.'],
      ['b) Clients and event attendees', 'Contact details, booking details, attendance records, and feedback you give us about our sessions.'],
      ['c) Financial wellbeing surveys', 'Responses to our surveys about financial wellbeing. Surveys are anonymous. We do not ask for your name or work email in the survey, and we do not ask for account numbers, credit card details, passwords or other sensitive financial identifiers. We ask you not to include personal details in free-text answers.'],
      ['d) Website and email data', 'Your IP address, device and browser type, pages visited, and whether you opened or clicked our emails, collected through cookies and similar technologies (see section 10).'],
      ['e) Special category data', 'We do not intend to collect special category data (for example health or ethnicity). If a participant volunteers this in a free-text answer, we will only use it in anonymised, aggregate form.'],
    ],
  },
  {
    title: '3. Where your data comes from',
    items: [
      'From you directly (forms, emails, calls, bookings, surveys)',
      'From your employer or institution, where they arrange a session for their staff or students',
      'From public sources such as company websites and professional networking profiles, for business-to-business outreach',
    ],
  },
  {
    title: '4. Why we use your data and our lawful basis',
    columns: ['Purpose', 'Lawful basis'],
    rows: [
      ['Responding to enquiries and booking sessions', 'Legitimate interests (running our business) or steps taken before entering a contract'],
      ['Delivering contracted services to employers and institutions', 'Contract'],
      ['Running anonymous surveys and producing aggregate reports', 'Legitimate interests (measuring and improving the impact of our programmes)'],
      ['Sending B2B marketing to corporate contacts', 'Legitimate interests, subject to your right to opt out at any time'],
      ['Sending marketing to individuals who have subscribed', 'Consent (you can withdraw at any time)'],
      ['Keeping business, tax and accounting records', 'Legal obligation'],
      ['Improving our website and services', 'Legitimate interests, or consent for non-essential cookies'],
    ],
    after: ['Where we rely on legitimate interests, we have considered the impact on you and do not use your data in ways you would not reasonably expect.'],
  },
  {
    title: '5. How survey data is handled',
    items: [
      'Surveys are anonymous and voluntary.',
      'We never share individual responses with an employer or institution.',
      'Results are only reported in aggregate, and only where a group has at least 10 responses. Where a group is smaller, we combine groups or withhold the result.',
      'Free-text answers are reviewed and anonymised before any quotes are shared.',
      'Surveys do not provide financial advice and are not a financial assessment of any individual.',
    ],
  },
  {
    title: '6. Who we share your data with',
    paragraphs: ['We do not sell personal data. We only share it with:'],
    items: [
      'Service providers (processors) who help us run our business under written agreements, for example website hosting, email and survey tools, video-conferencing and booking tools, and customer relationship management software.',
      'Professional advisers, such as accountants and legal advisers',
      'Regulators or authorities, where we are legally required to',
    ],
    after: ['If you attend a session arranged by your employer or institution, we may share attendance figures and aggregate feedback with them. We do not share your individual feedback or survey answers.'],
  },
  {
    title: '7. International transfers',
    paragraphs: ['Some of our providers may process data outside the UK. Where they do, we make sure appropriate safeguards are in place, such as the UK International Data Transfer Agreement or the UK Addendum to the EU Standard Contractual Clauses, or a UK adequacy decision.'],
  },
  {
    title: '8. How long we keep your data',
    columns: ['Data', 'Retention'],
    rows: [
      ['Enquiries that do not lead to a client relationship', 'Up to 24 months after our last contact'],
      ['Client and contract records', '6 years after the contract ends'],
      ['Anonymous survey data', 'Up to 3 years, in aggregate form'],
      ['Marketing subscriber details', 'Until you unsubscribe, then a suppression record to honour your opt-out'],
      ['Website analytics', 'Up to 26 months'],
    ],
  },
  {
    title: '9. Your rights',
    paragraphs: ['Under UK GDPR you have the right to:'],
    items: [
      'Be informed about how we use your data',
      'Access a copy of your personal data',
      'Rectify inaccurate data',
      'Erase your data in certain circumstances',
      'Restrict or object to our use of your data, including objecting to direct marketing at any time',
      'Data portability in certain circumstances',
      'Withdraw consent at any time, where we rely on it',
      'Not be subject to automated decisions with legal or similarly significant effects. We do not make such decisions.',
    ],
    after: [
      'To use any of these rights, email hello@genmoney.co.uk. We will respond within one month.',
      'Because surveys are anonymous, we cannot identify your individual response to access or delete it.',
    ],
  },
  {
    title: '10. Cookies',
    paragraphs: ['We use essential cookies to make the site work. We only use analytics or marketing cookies if you give consent through our cookie banner. You can change your choice at any time using the cookie settings link in the footer.'],
  },
  {
    title: '11. Security',
    paragraphs: ['We use appropriate technical and organisational measures to protect personal data, including access controls, secure storage and the use of reputable providers. No system is completely secure. If a breach affects your rights, we will tell you and the ICO where the law requires.'],
  },
  {
    title: '12. Under-18s',
    paragraphs: ['Our services are designed for adults in employment or higher education. Where a college arranges a session for learners under 18, the institution is responsible for the appropriate consents and safeguarding, and we do not collect personal data from learners beyond anonymous survey responses.'],
  },
  {
    title: '13. Marketing and opting out',
    paragraphs: ['Every marketing email contains an unsubscribe link, and you can opt out at any time by emailing hello@genmoney.co.uk. Business-to-business emails go only to corporate email addresses, with an opt-out in every message.'],
  },
  {
    title: '14. Complaints',
    paragraphs: ["If you are unhappy with how we use your data, please contact us first so we can try to put it right. You also have the right to complain to the Information Commissioner's Office (ICO): ico.org.uk, telephone 0303 123 1113."],
  },
  {
    title: '15. Changes to this notice',
    paragraphs: ['We may update this notice from time to time. The latest version will always be on our website, with the date of the last update at the top.'],
  },
];

export const privacyClosing = 'GenMoney is a financial education company. We do not provide financial advice and are not authorised or regulated by the FCA.';